import { Component, ElementRef, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import moment from 'moment';
import { BaseChartDirective } from 'ng2-charts';
import { DateRangeSelectorService } from 'src/app/reusables/date-range-selector/date-range-selector.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { TimeInterval } from 'src/shared/models/module-manager/module-manager-settings.models';
import { CxoneIntervalSelectorComponent } from '../../toolbar-items/cxone-interval-selector/cxone-interval-selector.component';
import { CxoneKPITrendModuleData, KPITrendModuleDisplay, TrendItem, ViewByOptions } from './cxone-kpi-trend.models';
import { CxoneKpiTrendService } from './cxone-kpi-trend.service';
import { NumericType, TimeType } from "src/shared/models/metric";
import { GroupSelectorService } from 'src/app/reusables/group-selector/group-selector.service';
import { AttributeSelectorService } from 'src/app/reusables/attribute-selector/attribute-selector.service';
import { ModuleContainerService } from 'src/app/dashboard/module-container/module-container.service';
import { AttributeSelectorModel, AttributeType, SelectedCategoryModel } from 'src/shared/models/attribute-selector.models';
import { MultiselectDropdownComponent } from '@niceltd/cxone-components/multiselect-dropdown';
import { ReportingSetDynamicFiltersService } from '../../../../reusables/reportingset-dynamic-filters/reportingset-dynamic-filters.service';

@Component({
    selector: 'cxone-kpi-trend',
    templateUrl: './cxone-kpi-trend.component.html',
    styleUrls: ['./cxone-kpi-trend.component.scss']
})
export class CxoneKpiTrendComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @ViewChild('KPITrendContent') KPITrendContent: ElementRef<HTMLDivElement>;
    @ViewChild(MultiselectDropdownComponent) cxMultiSelect: MultiselectDropdownComponent;
    isFirstLoad: boolean;
    highlightDefaultValue: boolean;
    noData = false;
    isNoMetric = false;
    intervalRangeTooWide = false;
    isCXOne = false;
    dataLoadSuccess = false;
    preventLoadingModal = true;
    public moduleDisplay: KPITrendModuleDisplay = KPITrendModuleDisplay.Compact;
    updateWidth = false;
    public chartConfig: ChartConfiguration;
    public metric: CxoneKPITrendModuleData;
    public metricName: string;
    public ViewBy: string;
    currentValue: string;
    trendDisplayValue: string;
    trendDifference: number;
    previousLabel: string;
    previousValue: string;
    public arrowDirection = true;
    public dynamicColor = '';
    intervalSelected: { id: number; order: number; name: string };
    unit: string;
    numericType: number;
    timeType: number;
    defaultRowTimer: any;
    trendShowDirection: boolean;
    ctx: CanvasRenderingContext2D;

    rightPadding = 20;
    bottomPadding = 11.37;
    leftPadding = 2.4;

    private kpiTrendContext = {
        viewStateLoaded: () => this.moduleDisplay === KPITrendModuleDisplay.Detailed,
        onIntervalChange: () => this.intervalSelectionChanged.bind(this),
        settingsSelectedInterval: null,
    };
    rangeData: { yMin: number; yMax: number; color: string; indicatorColor: string; }[];
    previousRange: { yMin: number; yMax: number; } = null;
    timer = null;
    addDelayToFillRectangle: boolean;
    trendItems: TrendItem[];
    selectedTrendItems = [];
    trendDropdownPlaceholder: string;
    attributeCategory: SelectedCategoryModel;
    constructor(
        private cvLocalizationService: CVLocalizationService,
        private service: CxoneKpiTrendService,
        private zone: NgZone,
        private config: CvConfigurationService,
        private dateRangeSelectorService: DateRangeSelectorService,
        private groupService: GroupSelectorService,
        private attributeService: AttributeSelectorService,
        private moduleContainerService: ModuleContainerService,
        private reportingSetDynamicFiltersService: ReportingSetDynamicFiltersService,
    ) {
        this.isCXOne = this.config.isCxOne;
    }

    async ngOnInit(): Promise<void> {
        this.isFirstLoad = true;
        await this.cvLocalizationService.GetSection("KPI_Trend_Module");
        this.moduleManager.setOptions({
            name: 'KPI Trend',
            widgetMode: false,
            onSettingsApplied: settings => this.zone.run(() => {
                this.moduleManager.container.toolbarHidden = (this.moduleDisplay === KPITrendModuleDisplay.Compact || this.moduleManager.moduleSettings.hideDatePicker);
                this.kpiTrendContext.settingsSelectedInterval = settings.intervalType;
                this.loadData(false);
            }),
            hasRefresh: true,
            onRefresh: isAutoRefresh => this.zone.run(() => this.loadData(isAutoRefresh, true)),
            canRefresh: () => !this.moduleManager.showLoader,
            onExport: (format) => this.zone.run(() => this.service.LoadDataForExport(this.moduleKey, format)),
            imageExportVisible: () => this.moduleManager?.moduleSettings?.selectedMetrics?.length ? true : false,
            onImageExport: () => this.exportPNG(),
        });

        this.moduleManager.addState({
            name: 'viewModeKPITrend',
            autoLoad: true,
            getStateData: () => this.moduleDisplay,
            onStateLoaded: (stateData) => {
                this.moduleDisplay = stateData ? stateData : KPITrendModuleDisplay.Compact;
                this.moduleManager.container.toolbarHidden = this.moduleDisplay === KPITrendModuleDisplay.Compact;
            },
        });

        this.moduleManager.addState({
            name: 'setInterval',
            autoLoad: true,
            getStateData: () => this.intervalSelected,
            onStateLoaded: (stateData) => {
                this.intervalSelected = stateData != null ? stateData : '';
            },
        });

        // assures menu items are set on the first load
        this.resetMenuItems();
        this.noData = false;
        this.moduleManager.addToolbarItem(CxoneIntervalSelectorComponent, this.kpiTrendContext);
        this.trendDropdownPlaceholder = this.cvLocalizationService.getCachedText("KPI_Trend_Module", "Select_max_5", "Select max 5 items");
    }

    /**
     * Resets the module menu items
     */
    resetMenuItems() {
        this.moduleManager.resetMenu();
        this.addMenuItems();
    }

    async addMenuItems() {
        const displayItems = [{
            name: this.cvLocalizationService.getCachedText("KPI_Trend_Module", "Compact"),
            icon: '',
            click: () => {
                this.zone.run(() => {
                    this.changeAndSaveModuleDisplay(KPITrendModuleDisplay.Compact);
                });
            }
        },
        {
            name: this.cvLocalizationService.getCachedText("KPI_Trend_Module", "Detailed"),
            icon: '',
            click: () => {
                this.zone.run(() => {
                    this.changeAndSaveModuleDisplay(KPITrendModuleDisplay.Detailed);
                });
            }
        }];
        this.moduleManager.addMenuItem({
            name: 'Widget Display',
            icon: 'chartType',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.dataLoadSuccess,
            subItems: displayItems,
            readOnlyEnabled: true
        } as ModuleManagerSubMenu);
    }

    changeAndSaveModuleDisplay(moduleDisplay: KPITrendModuleDisplay) {
        if (this.moduleDisplay === moduleDisplay) // going to the same display view as currently shown
            return;
        this.moduleDisplay = moduleDisplay;
        this.updateWidth = true;
        this.setDisplay();
        this.loadData(false, true);
        this.resetMenuItems();
        this.moduleManager.saveState('viewModeKPITrend');
        this.moduleManager.requestPublishNeeded();
    }

    async loadData(isAutoRefresh: boolean, preventLoadGroup?: boolean): Promise<any> {
        // call the AutoRefreshReady only when the loadData call
        // is successful and the data returned without any error.
        // if there's any exception occured then call handleError
        // method (check kpi.component).
        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;
        this.moduleManager.preventModuleMapUpdate();
        try {
            if (this.moduleManager.getModuleSettings().selectedMetrics.length > 0) {
                this.metric = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
                this.ViewBy = this.metric.ViewBy;
            }
            if (this.metric == null) {
                // this case is when there is no metric selected.
                this.moduleManager.showLoader = false;
                return;
            }
            else if (this.metric.Error === 1) {
                this.noData = true;
                this.intervalRangeTooWide = false;
            } else if (this.metric.Error === 2) {
                this.noData = false;
                this.intervalRangeTooWide = true;
            } else {
                this.noData = false;
                this.intervalRangeTooWide = false;
                this.moduleManager.setAutoRefreshReady();
                if (this.metric.KpiTrendData?.length) {
                    this.metricName = this.metric.MetricName;
                    this.currentValue = this.metric.DisplayAvgValue;
                    this.previousLabel = this.metric.PreviousLabel;
                    this.previousValue = this.metric.DisplayPreviousValue;
                    this.numericType = this.metric.NumericType;
                    this.timeType = this.metric.TimeType;
                    this.trendDisplayValue = this.metric.TrendDisplayValue !== null ? `${this.metric.TrendDisplayValue}%` : null;
                    this.trendShowDirection = this.metric.ShowDirection;
                    this.trendDifference = this.metric.Difference;
                    this.getUnit(this.numericType);
                    //Select dynamic color for arrow and value
                    this.dynamicColor = this.metric.Color;
                    //Set arrow direction
                    this.arrowDirection = this.metric.Difference > 0 ? true : false;
                    this.dataLoadSuccess = true;
                    if (!this.ViewBy?.includes('Company') && !preventLoadGroup) {
                        await this.loadGroups();
                    }
                } else {
                    this.noData = true;
                }
                this.updateChartData();
            }
        } catch (e) {
            this.moduleManager.handleLoadError(e, true);
        } finally {
            this.moduleManager.removeModuleMapPrevention();
        }
        this.isFirstLoad = false;
        this.moduleManager.showLoader = false;
    }

    formatValue(value: any, numericType: number, timeDisplayValue: string): string {
        if (value !== null) {
            value = parseFloat(value.toFixed(2)).toLocaleString();
            if (numericType === NumericType.Currency) {
                return '$' + value;
            } else if (numericType === NumericType.Percent) {
                return value + '%';
            } else if (numericType === NumericType.Time) {
                return timeDisplayValue;
            } else {
                return value; // No specific formatting for other types
            }
        } else {
            return 'N/A';
        }
    }

    getUnit(numericType: number) {
        if (numericType === NumericType.Currency) {
            this.unit = '$';
        } else if (numericType === NumericType.Percent) {
            this.unit = '%';
        } else if (numericType === NumericType.Time) {
            this.unit = 'Time';
        } else {
            this.unit = '';
        }
    }

    setDisplay() {
        this.moduleManager.container.toolbarHidden =
            (this.moduleDisplay === KPITrendModuleDisplay.Compact
                || this.moduleManager.moduleSettings.hideDatePicker);
        if (this.moduleDisplay === KPITrendModuleDisplay.Compact) {
            this.moduleManager.setSize(10, 10, false);
            this.moduleManager.setMinSize(8, 6);
            this.moduleManager.setMaxSize(32, 12);
        }
        else {
            this.moduleManager.setSize(12, 10, false);
            this.moduleManager.setMinSize(12, 7);
            this.moduleManager.setMaxSize(32, 12);
        }
    }

    async intervalSelectionChanged(intervalSelected): Promise<void> {
        this.intervalSelected = intervalSelected;
        await this.dateRangeSelectorService.saveIntervalSettings(
            `${this.moduleKey}_temp`,
            1,
            intervalSelected.id
        );
        await this.moduleManager.container.applySettings({ fromDetailedDisplay: true });
    }

    onMouseOut(event) {
        this.previousRange = null;
        this.chartObject.chart.update();
        clearTimeout(this.timer);
    }

    getChartConfig(): ChartConfiguration {
        this.rangeData = this.generateRanges(this.metric?.KpiTrendData[0]?.Objective?.Values || []);
        this.defaultRowTimer = null;
        this.addDelayToFillRectangle = false;
        const legendSpacePlugin = {
            beforeInit(chart) {
                const origFit = chart.legend.fit;
                chart.legend.fit = function fit() {
                    origFit.bind(chart.legend)();
                    this.height += 8;
                }
            }
        }
        return {
            type: "line",
            data: {
                datasets: [],
                labels: []
            },
            plugins: [{
                id: 'drawVerticalLine',
                afterDraw: (chart, args, options) => {
                    this.renderColorIndicators(chart);
                },
                afterRender: (chart) => {
                    if (this.highlightDefaultValue) {
                        this.addLastRangeForCountMetric(chart);
                        this.highlightDisplayValueRow(chart);
                    }
                },
            }, legendSpacePlugin],
            options: {
                onHover: this.onHoverChart.bind(this),
                datasets: {
                    line: {
                        spanGaps: true,
                    },
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    emptyChart: {
                        disableCxone: this.config.isCxOne,
                    },
                    legend: {
                        display: (this.ViewBy && !this.ViewBy.includes('Company')) || this.ViewBy === "" ? true : false,
                        position: 'top',
                        labels: {
                            boxWidth: 12,
                            boxHeight: 12,
                            color: 'black',
                            padding: 10,
                            font: {
                                size: 13
                            },
                        },
                        onClick: () => {
                            // Empty function to disable legend click
                        }
                    },
                    tooltip: {
                        enabled: true,
                        mode: 'point',
                        intersect: true,
                        caretPadding: 10,
                        displayColors: false,
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        borderWidth: 1,
                        borderColor: 'rgba(208, 210, 211, 1)',
                        titleFont: {
                            family: 'Inter',
                            size: 11,
                            weight: 'normal',
                        },
                        titleColor: '#8C95A5',
                        bodyFont: {
                            family: 'Open Sans, sans-serif',
                            size: 13,
                            weight: 'normal',
                        },
                        bodyColor: '#2E2E2E',
                        callbacks: {
                            title: (item) => moment((item[0].raw as any).x.toString()).format('D MMM'),
                            label: (item) => `${item.parsed.y.toLocaleString()} ${this.unit}`,
                        }
                    },
                },
                scales: {
                    x: {
                        distribution: 'series',
                        scaleLabel: {
                            display: false,
                        },
                        grid: {
                            display: false
                        },
                        ticks: {
                            autoSkip: true,
                            source: 'labels',
                            maxTicksLimit: 5,
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: this.unit,
                            font: {
                                family: 'Open Sans, sans-serif',
                                size: 12,
                                weight: '400',
                            },
                        },
                        grid: {
                            display: true,
                            color: 'rgba(232, 232, 232, 1)'
                        },
                        border: {
                            display: true,
                            dash: [4, 4],
                        },
                        suggestedMax: 10,
                        suggestedMin: 0,
                    }
                },
                layout: {
                    padding: {
                        bottom: this.bottomPadding,
                        right: this.rightPadding,
                        left: this.leftPadding,
                    }
                }
            },
        } as ChartConfiguration;
    }

    onHoverChart(event, elements, chart) {
        if (this.defaultRowTimer) {
            clearTimeout(this.defaultRowTimer);
        }
        const gridCoords = this.getSizeOfChart(chart, 'grid');
        let isHovering = false;

        if (this.rangeData?.length) {
            this.rangeData.forEach((range) => {
                const gridRowHeight = this.calculatePixelHeight(chart, range);
                const yMax = range.yMax > chart?.scales['y']?.max ? chart.scales['y']?.max : range.yMax;
                const newYAxisCoord = chart.scales['y'].getPixelForValue(yMax);

                if (event.x > gridCoords.x && event.x < gridCoords.x + gridCoords.w && event.y > newYAxisCoord && event.y < newYAxisCoord + gridRowHeight) {
                    isHovering = true;
                    if (elements?.length) {
                        this.addDelayToFillRectangle = true;
                    }
                    if (this.previousRange === null || (this.previousRange.yMax !== range.yMax && this.previousRange.yMin !== range.yMin) || this.addDelayToFillRectangle) {
                        chart.update();
                        if (this.timer) {
                            clearTimeout(this.timer);
                        }
                        this.timer = setTimeout(() => {
                            chart.ctx.fillStyle = range.color;
                            chart.ctx.fillRect(gridCoords.x, newYAxisCoord, gridCoords.w, gridRowHeight);
                            this.addDelayToFillRectangle = false;
                        }, this.addDelayToFillRectangle ? 500 : 100);
                    }
                    this.previousRange = range;
                }
            });
        }

        if (!isHovering) {
            this.previousRange = null;
            chart.update();
            if (this.timer) {
                clearTimeout(this.timer);
            }
        }
    }

    calculatePixelHeight(chart, coords) {
        const yMax = coords.yMax > chart?.scales['y']?.max ? chart.scales['y']?.max : coords.yMax;
        return chart.scales['y'].getPixelForValue(coords.yMin) - chart.scales['y'].getPixelForValue(yMax);
    }

    getSizeOfChart(chart, type: string) {
        if (chart && chart.chartArea) {
            if (type === 'grid') {
                return { x: chart.chartArea.left, y: chart.chartArea.top, w: chart.chartArea.width, h: chart.chartArea.height - (chart?.legend?.height || 0) };
            } else if (type === 'indicators') {
                const indicatorWidth = 4;
                const indicatorSpace = ((this.rightPadding - indicatorWidth) / 2);
                const indicatorLeft = chart.chartArea.left + chart.chartArea.width + indicatorSpace;
                return { x: indicatorLeft, y: chart.chartArea.top, w: indicatorWidth, h: chart.chartArea.height - (chart?.legend?.height || 0) };
            }
        }
    }

    renderColorIndicators(chart) {
        if (chart) {
            const indicatorCoords = this.getSizeOfChart(chart, 'indicators');
            if (this.rangeData?.length) {
                this.rangeData.forEach((range) => {
                    if (range.yMin > chart?.scales['y']?.max) return;
                    const gridRowHeight = this.calculatePixelHeight(chart, range);
                    const yMax = range.yMax > chart?.scales['y']?.max ? chart.scales['y']?.max : range.yMax;
                    const newYAxisCoord = chart.scales['y'].getPixelForValue(yMax);
                    chart.ctx.fillStyle = range.indicatorColor;
                    chart.ctx.fillRect(indicatorCoords.x, newYAxisCoord, indicatorCoords.w, gridRowHeight);
                });
            }
        }
    }

    highlightDisplayValueRow(chart) {
        this.highlightDefaultValue = false;
        const displayValue = this.metric.AvgValue;

        if (!this.rangeData?.length) return;
        const gridCoords = this.getSizeOfChart(chart, 'grid');

        this.rangeData.forEach((range) => {
            // added last check to avoid the display value range to be highlighted if it is greater than the max value of y-axis(going out of the chart)
            if (displayValue >= range.yMin && displayValue <= range.yMax && displayValue <= chart.scales['y']?.max) {
                const gridRowHeight = this.calculatePixelHeight(chart, range);
                const yMax = range.yMax > chart?.scales['y']?.max ? chart.scales['y']?.max : range.yMax;
                const newYAxisCoord = chart.scales['y'].getPixelForValue(yMax);
                if (this.defaultRowTimer) {
                    clearTimeout(this.defaultRowTimer);
                }
                this.defaultRowTimer = setTimeout(() => {
                    chart.ctx.fillStyle = range.color;
                    chart.ctx.fillRect(gridCoords.x, newYAxisCoord, gridCoords.w, gridRowHeight);
                }, 100);
            }
        });
    }

    updateChartData() {
        if (!this.metric)
            return;
        this.highlightDefaultValue = true;
        this.chartConfig = this.getChartConfig();
        const intervalType = this.moduleManager.getModuleSettings().intervalType;
        const isTimeIntervalShortDuration = intervalType === TimeInterval.FifteenMinute || intervalType === TimeInterval.HalfHour || intervalType === TimeInterval.Hour;

        const data: any[] = [];
        const labels: string[] = [];

        if (this.numericType === NumericType.Time) {
            this.chartConfig.options.scales.y.ticks = {};
            if (this.timeType === TimeType.Time) {
                this.metric.KpiTrendData.forEach((item, index) => {
                    const chartData = new Map(Object.entries(item.ChartData));
                    chartData.forEach((value, key) => {
                        let totalSeconds = null;
                        if (value !== null) {
                            // Convert time duration strings (e.g., "00:01:08") to seconds
                            const [hours, minutes, seconds] = value.split(':').map(Number);
                            totalSeconds = hours * 3600 + minutes * 60 + seconds;
                        }
                        data[index] = data[index] || [];
                        data[index].push(totalSeconds);
                        if (index === 0) {
                            this.formatLabel(labels, key, isTimeIntervalShortDuration);
                        }

                    });
                });
                this.chartConfig.options.scales.y.ticks.callback = (value) => this.formatDurationSeconds(value);
                this.chartConfig.options.plugins.tooltip.callbacks.label = (tooltipItem) => {
                    const value = tooltipItem.raw;
                    return this.formatDurationSeconds(value);
                };
            } else {
                this.metric.KpiTrendData.forEach((item, index) => {
                    const chartData = new Map(Object.entries(item.ChartData));
                    data[index] = data[index] || [];
                    chartData.forEach((value, key) => {
                        if (value !== null) {
                            const formattedStrNumber = value.replace(/,/g, '');
                            const num = parseFloat(formattedStrNumber);
                            isNaN(num) ? data[index].push(null) : data[index].push(num);
                        } else {
                            data[index].push(value);
                        }
                        if (index === 0) {
                            this.formatLabel(labels, key, isTimeIntervalShortDuration);
                        }
                    });

                });
                if (this.timeType === TimeType.Second) {
                    this.chartConfig.options.scales.y.ticks.callback = (value) => this.formatDurationSeconds(value);
                }
                if (this.timeType === TimeType.Minute) {
                    this.chartConfig.options.scales.y.ticks.callback = (value) => this.formatDurationMinutes(value);
                }
                if (this.timeType === TimeType.Hour) {
                    this.chartConfig.options.scales.y.ticks.callback = (value) => this.formatDurationHours(value);
                }
                this.chartConfig.options.plugins.tooltip.callbacks.label = (tooltipItem: any) => tooltipItem.raw.toLocaleString();
            }
            this.chartConfig.options.plugins.tooltip.callbacks.title = (tooltipItem: any) => tooltipItem[0].label;
        } else {
            this.metric.KpiTrendData.forEach((item, index) => {
                const chartData = new Map(Object.entries(item.ChartData));
                data[index] = data[index] || [];
                chartData.forEach((value, key) => {
                    data[index].push({ x: moment(key, "YYYY-MM-DD"), y: value });
                    if (index === 0) {
                        this.formatLabel(labels, key, isTimeIntervalShortDuration);
                    }
                });
            });
            if (isTimeIntervalShortDuration) {
                this.chartConfig.options.plugins.tooltip.callbacks.title = (tooltipItem) => {
                    const value = tooltipItem[0].label;
                    return value;
                };
            }
            this.chartConfig.options.plugins.tooltip.callbacks.label = (tooltipItem: any) => {
                return this.formatValue(tooltipItem.parsed.y, this.numericType, "");
            };
        }

        const trendColors = this.getTrendColors();
        this.chartConfig.data.datasets = [];
        this.metric.KpiTrendData.forEach((item, index) => {
            this.chartConfig.data.datasets.push({
                data: data[index] || [],
                label: item?.Group?.groupName,
                borderColor: trendColors[index % 5]?.borderColor,
                fill: this.ViewBy?.includes('Company') ? true : false,
                backgroundColor: trendColors[index % 5]?.backgroundColor,
                tension: 0.1,
                pointBackgroundColor: trendColors[index % 5]?.pointBackgroundColor,
                pointHoverBorderColor: trendColors[index % 5]?.pointHoverBorderColor,
                pointHoverBorderWidth: 5
            });
        });
        this.chartConfig.data.labels = labels;
    }

    // Function to convert seconds to "hh:mm:ss" format
    formatDurationSeconds(value) {
        const hours = Math.floor(value / 3600);
        const minutes = Math.floor((value % 3600) / 60);
        const seconds = value % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    // Function to convert minutes to "hh:mm:ss" format
    formatDurationMinutes(value) {
        const hours = Math.floor(value / 60);
        const remainingMinutes = value % 60;
        const totalSeconds = remainingMinutes * 60;
        const seconds = totalSeconds % 60;
        return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    // Function to convert hours to "hh:mm:ss" format
    formatDurationHours(value) {
        const totalSeconds = value * 3600;
        const hours = Math.floor(totalSeconds / 3600);
        const remainingSeconds = totalSeconds % 3600;
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    formatLabel(labels, value, isTimeIntervalShortDuration) {
        if (isTimeIntervalShortDuration) {
            labels.push(moment(value).utc().format("h:mm A"));
        } else {
            labels.push(moment(value, "YYYY-MM-DD").format("MMM DD"));
        }
    }

    // Function to generate ranges and add colors to each range
    generateRanges(ranges: number[] | []) {
        if (!ranges?.length) return [];
        const result = [];
        let currentMin = 0;
        const colorRange = this.metric.KpiTrendData[0]?.Objective?.ColorRange?.RgbaColor || [];
        const indicatorColorRange = this.metric.KpiTrendData[0]?.Objective?.ColorRange?.HexColor || [];
        const calculateCurrentMax = (range: number): number => {
            switch (this.numericType) {
                case NumericType.Currency:
                case NumericType.Number:
                    return Math.floor(range);
                case NumericType.Percent:
                    return Math.floor(range * 100);
                case NumericType.Time:
                    if (this.timeType === TimeType.Minute) {
                        return Number((range / 60).toFixed(2));
                    } else if (this.timeType === TimeType.Hour) {
                        return Number((range / 3600).toFixed(2));
                    }
                    return Math.floor(range);
            }
        };

        ranges.forEach((range, index) => {
            const currentMax = calculateCurrentMax(range);
            result.push({
                yMin: currentMin,
                yMax: currentMax,
                color: colorRange[index],
                indicatorColor: indicatorColorRange[index]
            });
            currentMin = currentMax + 0.01;
        });

        if (this.numericType === NumericType.Percent) {
            result.push({
                yMin: currentMin,
                yMax: 100,
                color: colorRange[colorRange.length - 1],
                indicatorColor: indicatorColorRange[indicatorColorRange.length - 1]
            });
        }
        return result;
    }

    // If the last range is up to the Y-axis max value, add a new range to fill the gap
    addLastRangeForCountMetric(chart) {
        // It will add range only for Count Metrix
        const yMax = chart?.scales?.y?.max;
        if (!this.rangeData?.length || this.numericType === NumericType.Percent || yMax <= this.rangeData[this.rangeData.length - 1].yMax) {
            return;
        }

        const lastRange = this.rangeData[this.rangeData.length - 1];
        const { ColorRange } = this.metric.KpiTrendData[0]?.Objective || {};
        const colorRange = ColorRange?.RgbaColor || [];
        const indicatorColorRange = ColorRange?.HexColor || [];

        this.rangeData.push({
            yMin: lastRange.yMax + 0.01,
            yMax,
            color: colorRange[colorRange.length - 1],
            indicatorColor: indicatorColorRange[indicatorColorRange.length - 1]
        });
        this.renderColorIndicators(chart);
    }

    async onCxOneSelectChange(event) {
        let ids = event?.selected?.filter(item => item.selected)?.map(item => item.id);
        if (!ids?.length) {
            ids = this.trendItems.filter((item, index) => index < 5).map(item => item.id);
            this.cxMultiSelect.toggleDropdown();
        }

        const pageName = this.moduleManager?.getModuleKey() + '_temp';
        if (Object.values(ViewByOptions).includes(this.ViewBy as ViewByOptions)) {
            const groups = {
                selectedGroup1Ids: [0],
                selectedGroup2Ids: [0],
                selectedGroup3Ids: [0],
                selectedGroup4Ids: [0],
                selectedGroup5Ids: [0],
                selectedGroupLevel: 0,
                toggleGroupData: false
            };

            const group = this.getGroupByView(this.ViewBy);
            groups[group.key] = ids?.length ? ids : [0];
            groups.selectedGroupLevel = this.moduleManager?.moduleSettings[group?.parent]?.SelectedGroupLevel;
            groups.toggleGroupData = this.moduleManager?.moduleSettings[group?.parent]?.toggleGroupData;
            await this.groupService.saveGroupSelector(pageName, this.getGroupType(this.ViewBy), groups);
        } else if (this.ViewBy === "" && this.moduleManager.loadedStateValues?.reportFilterableFields) {
            const filterableKey = this.moduleManager.moduleSettings.selectedFilterableGroupingDTO.ValueField;
            const settingsToSave = {};
            let reportFilterableFields = JSON.parse(this.moduleManager.loadedStateValues.reportFilterableFields)
            reportFilterableFields.Values[filterableKey] = ids;
            settingsToSave["reportFilterableFields"] = JSON.stringify(reportFilterableFields);
            this.moduleManager.loadedStateValues.reportFilterableFields = settingsToSave["reportFilterableFields"];
            await this.moduleManager.container.moduleContainerService.saveModuleStates(pageName.replace("_temp", ""), settingsToSave);
        } else {
            const model: AttributeSelectorModel = { Type: AttributeType.Data, SelectedCategories: [{ SelectedCategoryID: this.attributeCategory?.SelectedCategoryID, SelectedAttributeIDs: ids?.length ? ids : [0] }], GroupedCategoryID: this.attributeCategory?.SelectedCategoryID };
            await this.attributeService.saveSelectorModel(pageName, model);
        }

        await this.moduleContainerService.applyGenericSettings(132, this.moduleKey);
        this.moduleManager?.requestPublishNeeded();
        this.loadData(false, true);
        const selected = event?.selected?.filter(item => item.selected);
        this.selectedTrendItems = selected?.length ? selected : this.trendItems.filter((item, index) => index < 5);
    }

    async loadGroups() {
        const groupType = this.getGroupType(this.ViewBy);
        if (Object.values(ViewByOptions).includes(this.ViewBy as ViewByOptions)) {
            const response = await this.groupService.loadFilteredGroups(this.moduleManager?.getModuleKey() + '_temp', groupType);
            const groupKey = this.getGroupByView(this.ViewBy);
            this.trendItems = Object.values(response?.data).find(group => group.GroupLevelName === this.ViewBy).
                Groups.map(group => ({
                    name: group.GroupName,
                    id: group.GroupID,
                    selected: this.isTrendSelected(group?.GroupID, this.moduleManager.moduleSettings[groupKey?.parent][groupKey?.id] || [])
                }));
        } else if (this.ViewBy === "") {
            const reportingSetName = this.moduleManager.moduleSettings.selectedFilterableGroupingDTO.ValueSourceReportingSet.toString();
            const valueDisplayField = this.moduleManager.moduleSettings.selectedFilterableGroupingDTO.ValueDisplayField.toString();
            const filterableDisplayName = this.moduleManager.moduleSettings.selectedFilterableGroupingDTO.FilterableDisplayName.toString();
            let valueField = this.moduleManager.moduleSettings.selectedFilterableGroupingDTO.ValueField.toString();
            const savedSelection = this.moduleManager.moduleSettings.reportFilterableFields ? this.moduleManager.moduleSettings.reportFilterableFields.Values[valueField] : JSON.parse(this.moduleManager.loadedStateValues?.reportFilterableFields)?.Values[valueField];
            valueField = valueField.includes(filterableDisplayName) ? valueField.replace(filterableDisplayName + "_", "") : valueField;

            const response = await this.reportingSetDynamicFiltersService.loadReportFilters(reportingSetName, valueDisplayField, valueField);
            this.trendItems = response?.map(value => ({
                name: value.DisplayValue,
                id: value.Value,
                selected: this.checkAdditionalFiltersSelection(value.Value, savedSelection)
            }));
        } else {
            const attributes = await this.attributeService.loadSelectorModel(this.moduleManager?.getModuleKey() + '_temp', 0);
            this.attributeCategory = attributes?.SelectedCategories[0];

            if (this.attributeCategory) {
                const values = await this.attributeService.loadValues(this.attributeCategory?.SelectedCategoryID, 0);
                this.trendItems = values?.map(attr => ({
                    name: attr.Name,
                    id: attr.ID,
                    selected: this.isTrendSelected(attr.ID, this.attributeCategory?.SelectedAttributeIDs)
                }));
            }
        }
        if (this.trendItems?.length && !this.trendItems.some(item => item.selected)) {
            this.selectedTrendItems = this.trendItems.filter((item, index) => index < 5);
        } else {
            this.selectedTrendItems = this.trendItems?.filter(item => item.selected);
        }
    }

    isTrendSelected(id: number, selectedItems: number[]): boolean {
        return selectedItems.includes(id);
    }

    checkAdditionalFiltersSelection(id: any, selectedItems: any[]): boolean {
        return selectedItems.includes(id);
    }

    getGroupByView(viewBy: string) {
        switch (viewBy) {
            case 'Campaign':
                return { parent: 'recSelectorDTO', id: '_SelectedGroup3IDList', key: 'selectedGroup3Ids' };
            case 'Skill':
                return { parent: 'recSelectorDTO', id: '_SelectedGroup2IDList', key: 'selectedGroup2Ids' };
            case 'Team':
                return { parent: 'empSelectorDTO', id: '_SelectedGroup2IDList', key: 'selectedGroup2Ids' };
            case 'Agent':
                return { parent: 'empSelectorDTO', id: '_SelectedGroup1IDList', key: 'selectedGroup1Ids' };
            default:
                return { parent: 'empSelectorDTO', id: '_SelectedGroup1IDList', key: 'selectedGroup1Ids' };
        }
    }

    // Function to check which group a value belongs to
    getGroupType(value: string): number {
        const Contactgroup = ["Skill", "Campaign"];
        if (Contactgroup.includes(value)) {
            return 1;
        } else
            return 0;
    }

    getTrendColors() {
        const generateColor = (borderColor, backgroundColor, pointBackgroundColor, pointHoverBorderColor) => {
            return {
                borderColor,
                backgroundColor: this.ViewBy?.includes('Company') ? `${backgroundColor},0.1)` : `${backgroundColor}, 1)`,
                pointBackgroundColor,
                pointHoverBorderColor,
            };
        };

        return [
            generateColor('rgb(0, 124, 190)', 'rgba(0, 124, 190', 'rgba(0, 124, 190, 1)', 'rgba(0, 124, 190, 0.3)'),
            generateColor('rgb(231, 139, 55)', 'rgba(231, 139, 55', 'rgba(231, 139, 55, 1)', 'rgba(231, 139, 55, 0.3)'),
            generateColor('rgb(205, 74, 129)', 'rgba(205, 74, 129', 'rgba(205, 74, 129, 1)', 'rgba(205, 74, 129, 0.3)'),
            generateColor('rgb(82, 79, 183)', 'rgba(82, 79, 183', 'rgba(82, 79, 183, 1)', 'rgba(82, 79, 183, 0.3)'),
            generateColor('rgb(83, 178, 173)', 'rgba(83, 178, 173', 'rgba(83, 178, 173, 1)', 'rgba(83, 178, 173, 0.3)')
        ];
    }

    exportPNG(): void {
        const date = (new Date()).toString('yyyy_MM_dd_HH_mm_ss');
        const fileName = this.cvLocalizationService.getCachedText("KPI_Trend_Module", "KPI_Trend", "KPI Trend Widget")
            .replace(/\s+/g, '-').toLowerCase() + '_(' + date + ').png';

        const canvas = this.chartObject.chart.canvas;
        const exportCanvas = document.createElement('canvas');
        const exportDiv = this.KPITrendContent.nativeElement;
        const context = exportCanvas.getContext('2d');
        const rect = exportDiv.getBoundingClientRect();

        const pixelRatio = window.devicePixelRatio || 1; // Get device pixel ratio
        exportCanvas.width = rect.width * pixelRatio;
        exportCanvas.height = rect.height * pixelRatio;

        if (context) {
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

            // Draw the chart on the canvas
            context.drawImage(canvas, 0, 60);

            context.font = 'bold 13px Arial';
            context.fillStyle = '#000000';
            context.fillText(this.metricName, 20, 25);
            context.fillText(this.currentValue, 22, 50);

            context.font = '13px Arial';
            context.fillStyle = '#000000';
            context.fillText(this.previousLabel, 200, 25);
            context.fillText(this.previousValue, 200, 50);
            if (this.trendDifference && this.trendDisplayValue) {
                context.fillStyle = this.dynamicColor;
                context.fillText(this.trendDisplayValue, 120, 50);
            }
            if (this.trendShowDirection) {
                const icon = new Image();
                icon.src = `../App_Themes/CXone/icons/${this.arrowDirection ? 'up-arrow' : 'down-arrow'}.svg`;

                icon.onload = () => {
                    context.drawImage(icon, 100, 40, 13, 12);
                    const url = exportCanvas.toDataURL('image/png');
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    link.click();
                    // cleanup
                    URL.revokeObjectURL(link.href);
                    link.remove();
                };
            } else {
                const url = exportCanvas.toDataURL('image/png');
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                link.click();
                // cleanup
                URL.revokeObjectURL(link.href);
                link.remove();
            }
        }
    }
}
